import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { AnimationOptions } from 'ngx-lottie';
import { InfiniteScrollListDirective, InfiniteScrollListItemDirective, InputComponent } from '@libs/ui-lib';
import { FfdNoteGroup, FfdNoteItem, PLACEHOLDER_FFD_NOTE_ITEM } from '@libs/ffd-notes';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { FfdNoteVisitComponent } from '../ffd-note-visit';

export const NUMBER_OF_LOADING_SKELETONS = 5;
export const NUMBER_OF_INFINITE_SCROLL_LOADING_SKELETONS = 3;

@Component({
  selector: 'lib-ffd-notes-list',
  imports: [
    CommonModule,
    TranslateModule,
    FfdNoteVisitComponent,
    CdkScrollable,
    InputComponent,
    SvgIconComponent,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
  ],
  templateUrl: './ffd-notes-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './ffd-notes-list.component.scss',
})
export class FfdNotesListComponent {
  private readonly tagPusherService = inject(TagPusherService);

  public readonly items = input<FfdNoteGroup[]>([]);
  public readonly searchTerm = input<string | null>(null);
  public readonly isLoading = input(false);
  public readonly infiniteScrollLoading = input(false);
  public readonly selectedNoteId = input<string | null>(null);

  public readonly searchTermChange = output<string | null>();
  public readonly endReached = output<void>();
  public readonly noteClick = output<string>();
  public readonly noteEdit = output<string>();
  public readonly noteDelete = output<string>();

  protected readonly placeholderFfdNoteItem: FfdNoteItem = PLACEHOLDER_FFD_NOTE_ITEM;

  protected readonly loadingSkeletons = computed(() => Array.from({ length: NUMBER_OF_LOADING_SKELETONS }).fill(0));
  protected readonly infiniteScrollLoadingSkeletons = computed(() =>
    Array.from({ length: NUMBER_OF_INFINITE_SCROLL_LOADING_SKELETONS }).fill(0),
  );

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public onSearchTermChange(value: string | undefined): void {
    this.searchTermChange.emit(value || null);
  }

  public onClearSearchTermClick(): void {
    this.searchTermChange.emit(null);
  }

  public onEndReached(): void {
    this.endReached.emit();
  }

  public onNoteClick(noteId: string): void {
    this.noteClick.emit(noteId);
  }

  public onEditNoteClick(noteId: string): void {
    this.tagPusherService.pushTag({
      event: TagEvent.FfdNotesNoteEdit,
      location: 'home',
    });
    this.noteEdit.emit(noteId);
  }

  public onDeleteNoteClick(noteId: string): void {
    this.tagPusherService.pushTag({
      event: TagEvent.FfdNotesNoteDelete,
      location: 'home',
    });
    this.noteDelete.emit(noteId);
  }
}
